// Player preferences that belong to the world and the UI kit (persisted per
// browser). Every read and write tolerates storage being unavailable.
import { useSyncExternalStore } from 'react';

const KEYS = {
  sfx: 'scotus.sfx',
  readingFont: 'scotus.reading_font',
  motion: 'scotus.reduce_motion',
} as const;

const listeners = new Set<() => void>();
function emit(): void { listeners.forEach((fn) => fn()); }
function read(key: string): string | null {
  try { return window.localStorage.getItem(key); } catch { return null; }
}
function write(key: string, value: string | null): void {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch { /* private mode: the choice lasts this page only */ }
  emit();
}

/** Sound effects on unless the player turned them off. */
export function sfxEnabled(): boolean { return read(KEYS.sfx) !== '0'; }
export function setSfxEnabled(on: boolean): void { write(KEYS.sfx, on ? '1' : '0'); }

/** Reading font: Schola for all UI text instead of the pixel faces. */
export function readingFont(): boolean { return read(KEYS.readingFont) === '1'; }
export function setReadingFont(on: boolean): void {
  write(KEYS.readingFont, on ? '1' : '0');
  applyReadingFont();
}
export function applyReadingFont(): void {
  if (typeof document !== 'undefined') document.documentElement.dataset.font = readingFont() ? 'reading' : 'pixel';
}

/** Reduced motion: null follows the OS; true or false is the player's override. */
export function motionOverride(): boolean | null {
  const v = read(KEYS.motion);
  return v === '1' ? true : v === '0' ? false : null;
}
export function setMotionOverride(v: boolean | null): void { write(KEYS.motion, v === null ? null : v ? '1' : '0'); }

/** Call fn whenever a preference changes (in this tab). */
export function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** Re-render on any preference change. */
export function usePrefs(): { sfx: boolean; readingFont: boolean; motion: boolean | null } {
  const snap = useSyncExternalStore(subscribe, () => `${read(KEYS.sfx)}|${read(KEYS.readingFont)}|${read(KEYS.motion)}`, () => '');
  void snap;
  return { sfx: sfxEnabled(), readingFont: readingFont(), motion: motionOverride() };
}
